import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { usePlanetStore } from '../store/planetStore';
import { usePlayerStore, selectEffectiveTier } from '../store/playerStore';
import { useUniverseStore } from '../store/universeStore';
import { useNow } from '../hooks/useNow';
import { usePlanetResource } from '../hooks/usePlanetResource';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';
import { tradeResources, type PlanetActionOptions } from '../services/planet.service';
import { purchaseTradeTokens } from '../services/premium.service';
import { formatCountdown, formatFullNumber } from '../utils/formatters';
import { Button } from '../components/ui/Button';
import { Icon } from '../components/ui/Icon';
import { Panel } from '../components/ui/Panel';
import PageLoadingState from '../components/PageLoadingState';
import { Input } from '../components/ui/Input';
import { Select, SelectOption } from '../components/ui/Select';
import { RESOURCE_LABELS } from '../utils/resourceQuantities';
import DarkMatterAmount from '../components/DarkMatterAmount';
import { linkButtonClasses } from '../components/ui/buttonStyles';
import {
    BUILDING_NAMES,
    MIN_TRADE_AMOUNT,
    PIRATE_TOKEN_WINDOW_MS,
    TRADEABLE_RESOURCES,
    TRADE_TOKEN_COST_PER_TRADE,
    TRADE_TOKEN_PRICE,
    TRADE_WINDOW_MS,
    TRADES_PER_LEVEL_PER_WINDOW,
    type TradeableResource,
} from '@kaeliux/shared';
import {
    calculatePirateTokenDailyCap,
    calculateTradeOutput,
    currentPirateTokenWindowStart,
    currentTradeWindowStart,
    extractBuildingLevels,
    resolvePirateTokenWindowCount,
    resolveTradeWindowCount,
} from '@kaeliux/game-engine';


function TradeCenterPageContent() {
    const { state: { planet, isLoading } } = usePlanetResource();
    const premium = usePlayerStore((s) => s.premium);
    const points = usePlayerStore((s) => s.points);
    const effectiveTier = usePlayerStore(selectEffectiveTier);
    const { resourceMultiplier, speedMultiplier, sunAges } = useUniverseStore();
    const now = useNow();
    const { openDialog } = useConfirmDialog();

    const [from, setFrom] = useState<TradeableResource>('minerals');
    const [to, setTo] = useState<TradeableResource>('metal');
    const [amountInput, setAmountInput] = useState('');
    const [isTrading, setIsTrading] = useState(false);
    const [tradeError, setTradeError] = useState<string | null>(null);

    const [tokenQuantityInput, setTokenQuantityInput] = useState('10');
    const [isBuyingTokens, setIsBuyingTokens] = useState(false);
    const [buyError, setBuyError] = useState<string | null>(null);

    const options: PlanetActionOptions = { resourceMultiplier, sunAges, speedMultiplier };

    if (isLoading || !planet) {
        return (
            <div className="min-h-screen bg-space-900 flex items-center justify-center">
                <PageLoadingState message={`Loading ${BUILDING_NAMES.tradeCenter}…`} />
            </div>
        );
    }

    const tradeCenterLevel = extractBuildingLevels(planet.buildings).tradeCenter ?? 0;
    if (tradeCenterLevel === 0) {
        return <Navigate to="/zone/industry?highlight=tradeCenter" replace />;
    }

    const nowDate = new Date(now);
    const windowCount = resolveTradeWindowCount(planet.tradeWindow, nowDate);
    const windowLimit = tradeCenterLevel * TRADES_PER_LEVEL_PER_WINDOW;
    const windowRemaining = windowLimit - windowCount;
    const windowResetMs = currentTradeWindowStart(nowDate).getTime() + TRADE_WINDOW_MS - now;

    const tradeTokens = premium?.tradeTokens ?? 0;
    const darkMatter = premium?.darkMatter ?? 0;

    // How many of today's pirate-earned tokens have already been credited,
    // and the cap they're counted against — surfaced so a won raid that
    // yields no token (roll missed, loot too small, or the cap is already
    // hit) reads as an explained outcome, not a silent bug.
    const pirateTokenDailyCap = calculatePirateTokenDailyCap(points?.total ?? 0, effectiveTier);
    const pirateTokensEarnedToday = resolvePirateTokenWindowCount(premium?.pirateTokenWindow, nowDate);
    const pirateTokenWindowResetMs = currentPirateTokenWindowStart(nowDate) + PIRATE_TOKEN_WINDOW_MS - now;

    const amount = Math.max(0, Math.floor(Number(amountInput) || 0));
    const hasAmount = amount > 0;
    // from !== to is guaranteed by the From/To pickers (the To options
    // exclude whatever From is set to) and by TradeResourcesSchema
    // server-side, but calculateTradeOutput throws for a same-resource pair
    // — guarding it here defensively means a future regression in that
    // picker logic degrades to "no output shown," not an uncaught crash
    // mid-render.
    const output = hasAmount && from !== to ? calculateTradeOutput(from, to, amount) : 0;
    const tokenCost = hasAmount ? TRADE_TOKEN_COST_PER_TRADE : 0;
    const available = planet.resources[from].current;
    const toCapacity = planet.resources[to].capacity;
    const toCurrent = planet.resources[to].current;
    const effectiveOutput = hasAmount ? Math.min(output, Math.max(0, toCapacity - toCurrent)) : 0;

    const hasWindowSlot = windowRemaining > 0;
    const meetsMinimum = !hasAmount || amount >= MIN_TRADE_AMOUNT;
    const hasEnoughResource = amount <= available;
    const hasEnoughTokens = tokenCost <= tradeTokens;
    const wouldWasteEverything = hasAmount && effectiveOutput <= 0;
    const wouldWastePartial = hasAmount && effectiveOutput > 0 && effectiveOutput < output;

    const disabled = !hasAmount || !hasWindowSlot || !meetsMinimum || !hasEnoughResource || !hasEnoughTokens || wouldWasteEverything || isTrading;
    const disabledReason = !hasWindowSlot
        ? `No free trade this window — resets in ${formatCountdown(windowResetMs)}`
        : !hasAmount
            ? null
            : !meetsMinimum
                ? `Minimum trade amount is ${formatFullNumber(MIN_TRADE_AMOUNT)}`
                : !hasEnoughResource
                    ? `Insufficient ${RESOURCE_LABELS[from]}`
                    : !hasEnoughTokens
                        ? `Not enough trade tokens (needs ${tokenCost}, has ${tradeTokens})`
                        : wouldWasteEverything
                            ? `${RESOURCE_LABELS[to]} storage is full — this trade would be entirely wasted`
                            : null;

    function handleFromChange(next: TradeableResource) {
        setFrom(next);
        if (next === to) setTo(from);
    }

    async function handleTrade() {
        setIsTrading(true);
        setTradeError(null);
        try {
            const result = await tradeResources(planet!.id, from, to, amount, options);
            usePlanetStore.setState({ currentPlanet: result.planet });
            usePlayerStore.setState({ premium: result.premium });
            setAmountInput('');
        } catch (err) {
            setTradeError(err instanceof Error ? err.message : 'Trade failed');
        } finally {
            setIsTrading(false);
        }
    }

    function requestTrade() {
        openDialog({
            title: 'Confirm trade',
            message: `Give up ${formatFullNumber(amount)} ${RESOURCE_LABELS[from]} to receive ${formatFullNumber(effectiveOutput)} ${RESOURCE_LABELS[to]}? Costs ${tokenCost} trade token${tokenCost === 1 ? '' : 's'}.`,
            warning: wouldWastePartial
                ? `${RESOURCE_LABELS[to]} storage is nearly full — only ${formatFullNumber(effectiveOutput)} of ${formatFullNumber(output)} will actually be credited. The rest is lost.`
                : undefined,
            confirmLabel: 'Trade',
            onConfirm: () => { handleTrade().catch(() => { }); },
        });
    }

    const tokenQuantity = Math.max(0, Math.floor(Number(tokenQuantityInput) || 0));
    const tokenPrice = tokenQuantity * TRADE_TOKEN_PRICE;
    const canAffordTokens = tokenQuantity > 0 && tokenPrice <= darkMatter;

    async function handleBuyTokens() {
        setIsBuyingTokens(true);
        setBuyError(null);
        try {
            const result = await purchaseTradeTokens(tokenQuantity);
            usePlayerStore.setState({ premium: result.premium });
        } catch (err) {
            setBuyError(err instanceof Error ? err.message : 'Purchase failed');
        } finally {
            setIsBuyingTokens(false);
        }
    }

    return (
        <div className="bg-transparent flex flex-col">
            <main className="flex-1 w-full max-w-3xl mx-auto px-4 py-4 flex flex-col gap-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <h1 className="text-heading font-bold text-content-primary">{BUILDING_NAMES.tradeCenter}</h1>
                    <div className="flex items-center gap-3">
                        <span className="text-content-muted text-body" data-testid="trade-window-status">
                            Trades: {windowCount} / {windowLimit} · Resets in {formatCountdown(windowResetMs)} · Tokens: {tradeTokens}
                        </span>
                        <Link to="/planet" className={linkButtonClasses('secondary', 'sm')}>← Back to Planet</Link>
                    </div>
                </div>

                <Panel variant="solid" padding="md" className="flex flex-col gap-4" data-testid="trade-panel">
                    <div className="flex items-center gap-3">
                        <div className="flex-1">
                            <Select
                                id="trade-from"
                                data-testid="trade-from-select"
                                label="From"
                                value={from}
                                onChange={(e) => handleFromChange(e.target.value as TradeableResource)}
                            >
                                {TRADEABLE_RESOURCES.map((r) => (
                                    <SelectOption key={r} value={r}>{RESOURCE_LABELS[r]}</SelectOption>
                                ))}
                            </Select>
                            <p className="text-content-muted text-label mt-1">
                                Available: {formatFullNumber(available)}
                            </p>
                        </div>
                        <Button
                            variant="secondary"
                            size="md"
                            className="w-9 h-9 mt-5 !px-0 shrink-0"
                            onClick={() => { setFrom(to); setTo(from); }}
                            aria-label="Swap resources"
                        >
                            <Icon name="trade-token" size={18} aria-hidden="true" />
                        </Button>
                        <div className="flex-1">
                            <Select
                                id="trade-to"
                                data-testid="trade-to-select"
                                label="To"
                                value={to}
                                onChange={(e) => setTo(e.target.value as TradeableResource)}
                            >
                                {TRADEABLE_RESOURCES.filter((r) => r !== from).map((r) => (
                                    <SelectOption key={r} value={r}>{RESOURCE_LABELS[r]}</SelectOption>
                                ))}
                            </Select>
                            <p className="text-content-muted text-label mt-1">
                                {hasAmount ? `You'll receive: ${formatFullNumber(effectiveOutput)}` : ' '}
                            </p>
                        </div>
                    </div>

                    <div>
                        <Input
                            id="trade-amount"
                            data-testid="trade-amount-input"
                            type="number"
                            min={MIN_TRADE_AMOUNT}
                            label={`Amount of ${RESOURCE_LABELS[from]} to trade away (minimum ${formatFullNumber(MIN_TRADE_AMOUNT)})`}
                            value={amountInput}
                            onChange={(e) => setAmountInput(e.target.value)}
                            placeholder="0"
                        />
                    </div>

                    {hasAmount && (
                        <p className="text-content-muted text-body">
                            Cost: {tokenCost} trade token{tokenCost === 1 ? '' : 's'}
                        </p>
                    )}
                    {wouldWastePartial && (
                        <p className="text-status-warning text-label" data-testid="trade-capacity-warning">
                            {RESOURCE_LABELS[to]} storage is nearly full — only {formatFullNumber(effectiveOutput)} of {formatFullNumber(output)} will be credited.
                        </p>
                    )}

                    {tradeError && <p className="text-status-danger text-label">{tradeError}</p>}
                    <div className="flex items-center justify-between gap-3">
                        {disabledReason && !isTrading && (
                            <span className="text-content-muted text-label">{disabledReason}</span>
                        )}
                        <Button
                            data-testid="trade-submit-button"
                            variant="primary"
                            size="md"
                            className="ml-auto"
                            onClick={requestTrade}
                            disabled={disabled}
                        >
                            {isTrading ? 'Trading…' : 'Trade'}
                        </Button>
                    </div>
                </Panel>

                <Panel variant="solid" padding="md" className="flex flex-col gap-3" data-testid="buy-tokens-panel">
                    <div>
                        <h2 className="text-content-secondary font-semibold">Buy Trade Tokens</h2>
                        <p className="text-content-muted text-body">
                            {TRADE_TOKEN_PRICE} Dark Matter per token · Balance: <DarkMatterAmount amount={darkMatter} />
                        </p>
                        <p className="text-content-muted text-body mt-1">
                            Also earned from Pirate flights: a won raid has a 50% chance of +1 token, but only if it
                            loots enough to be worth it — and pirate-earned tokens are capped per day, scaling with
                            your score and premium tier.
                        </p>
                        <p className="text-content-muted text-label mt-1" data-testid="pirate-token-earn-status">
                            Pirate raids today: {pirateTokensEarnedToday} / {pirateTokenDailyCap} tokens earned · resets in {formatCountdown(pirateTokenWindowResetMs)}
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <Input
                            data-testid="buy-tokens-quantity-input"
                            type="number"
                            min={1}
                            className="w-24"
                            value={tokenQuantityInput}
                            onChange={(e) => setTokenQuantityInput(e.target.value)}
                        />
                        <span className="text-content-muted text-body">
                            = <DarkMatterAmount amount={tokenPrice} />
                        </span>
                        <Button
                            data-testid="buy-tokens-button"
                            variant="secondary"
                            size="md"
                            className="ml-auto"
                            onClick={handleBuyTokens}
                            disabled={!canAffordTokens || isBuyingTokens}
                        >
                            {isBuyingTokens ? 'Buying…' : canAffordTokens ? 'Buy' : 'Not enough Dark Matter'}
                        </Button>
                    </div>
                    {buyError && <p className="text-status-danger text-label">{buyError}</p>}
                </Panel>
            </main>
        </div>
    );
}

export default function TradeCenterPage() {
    const { profile } = useAuthStore();
    const activePlanetId = usePlanetStore((s) => s.activePlanetId);
    const planetId = activePlanetId ?? profile?.mainPlanetId ?? '';

    if (!profile || !planetId || !profile.race) {
        return (
            <div className="min-h-screen bg-space-900 flex items-center justify-center">
                <PageLoadingState message="Loading…" />
            </div>
        );
    }

    return <TradeCenterPageContent />;
}


